import { resolveElplanBidErrorAlert } from 'api/elplan/elplan.api'
import type { PlanValues } from 'api/elplan/elplan.types'
import { useTranslation } from 'react-i18next'
import alertStore from 'store/alert/alert'
import Datetime from 'utils/datetime/datetime'
import type { FormattedBidWithIdAndTitle } from '../../../DayAheadBidManagement/DayAheadBidManagement.helper'

type SpotBidWrite = (bids: PlanValues[]) => Promise<unknown>

type UseSpotBidSaveOptions = {
  spotBids: FormattedBidWithIdAndTitle[]
  createBid: SpotBidWrite
  updateBid: SpotBidWrite
}

export function useSpotBidSave({ spotBids, createBid, updateBid }: UseSpotBidSaveOptions) {
  const { t } = useTranslation()

  return async (planValues: PlanValues[]): Promise<boolean> => {
    const saves: Promise<unknown>[] = []
    if (spotBids.length === 0) {
      saves.push(createBid(planValues))
    } else {
      const bidIdsByStart = new Map(spotBids.map((bid) => [Datetime.toISOString(bid.startTime), bid.id]))
      const bidsWithCreatedIds = planValues.map((bid) => ({
        ...bid,
        id: bidIdsByStart.get(Datetime.toISOString(bid.start_time)) ?? bid.id,
      }))
      const existingBids = bidsWithCreatedIds.filter((bid) => typeof bid.id === 'number')
      const missingBids = bidsWithCreatedIds.filter((bid) => typeof bid.id !== 'number')
      if (existingBids.length > 0) saves.push(updateBid(existingBids))
      if (missingBids.length > 0) saves.push(createBid(missingBids))
    }

    const results = await Promise.allSettled(saves)
    const firstFailure = results.find((result) => result.status === 'rejected')
    if (firstFailure?.status === 'rejected') {
      const fallbackKey = 'Spot bids were not saved. Try again.'
      const { message, alertKey } = resolveElplanBidErrorAlert(firstFailure.reason, fallbackKey, t)
      alertStore.error(alertKey ? message : t(fallbackKey), undefined, alertKey)
      return false
    }

    if (spotBids.length === 0) alertStore.success(`${t('Successfully created')} ${t('spot bid')}`)
    else if (saves.length > 0) alertStore.success(t('Spot bids saved'))
    return true
  }
}
